<template>
  <div class="log-rate-widget box" :class="{ 'is-loading': loading }">
    <!-- Header -->
    <div class="log-rate-header">
      <div class="log-rate-header-main">
        <span class="log-rate-title">{{ $t('logRateStats') || 'Log Rate Statistics' }}</span>
      </div>
      <div class="log-rate-header-actions">
        <button
          class="log-rate-refresh-btn"
          :disabled="loading"
          @click="refresh"
        >
          <octicon :icon="syncIcon" :class="{ 'spin': loading }" />
        </button>
      </div>
    </div>

    <!-- Error State -->
    <div v-if="error" class="notification is-danger is-light">
      <p>{{ $t('errorFetchingLogRate') || 'Error fetching log rate stats' }}</p>
      <p class="is-size-7">{{ error }}</p>
      <button class="button is-small mt-2" @click="refresh">
        <octicon :icon="syncIcon" />
        <span>{{ $t('retry') || 'Retry' }}</span>
      </button>
    </div>

    <!-- Stats Display -->
    <div v-else class="log-rate-content">
      <!-- Quota Progress -->
      <div class="quota-section">
        <div class="quota-header">
          <span class="quota-title">{{ $t('dailyQuota') || 'Daily Quota' }}</span>
          <div class="quota-header-right">
            <span class="quota-usage">{{ formatBytes(stats.quotaUsed) }} / {{ formatBytes(stats.quotaMax) }}</span>
            <button
              class="edit-quota-btn"
              :title="$t('editQuota') || 'Thay đổi quota'"
              @click="showQuotaModal = true"
            >
              <octicon :icon="pencilIcon" />
            </button>
          </div>
        </div>
        <div v-if="stats.quotaEnabled" class="progress-container">
          <progress
            class="progress"
            :class="getQuotaProgressClass(stats.quotaPercent)"
            :value="stats.quotaPercent"
            max="100"
          />
          <span class="progress-label">{{ formatPercent(stats.quotaPercent) }}</span>
        </div>
        <p v-if="stats.quotaPercent >= 90" class="quota-warning has-text-danger is-size-7">
          <octicon :icon="alertIcon" />
          {{ $t('quotaWarning') || 'Warning: Quota nearly exhausted! Logs will be dropped.' }}
        </p>
      </div>

      <!-- Realtime -->
      <div class="stats-section">
        <div class="stats-section-header">
          <div>
            <h4 class="stats-section-title">{{ $t('logRate.realtimeTitle') || 'Thời gian thực / Realtime' }}</h4>
            <p class="stats-section-description">{{ $t('logRate.realtimeDescription') || '1 giây, 1 phút, 1 giờ, 24 giờ gần nhất' }}</p>
          </div>
        </div>
        <div class="stats-grid stats-grid--realtime">
          <div class="stat-card stat-card--realtime">
            <div class="stat-header">
              <octicon :icon="pulseIcon" />
              <span>{{ $t('perSecond') || '/giây' }}</span>
            </div>
            <div class="stat-value">{{ formatNumber(stats.logsPerSecond) }}</div>
          </div>
          <div class="stat-card stat-card--realtime">
            <div class="stat-header">
              <octicon :icon="clockIcon" />
              <span>{{ $t('perMinute') || '/phút' }}</span>
            </div>
            <div class="stat-value">{{ formatNumber(stats.logsPerMinute) }}</div>
          </div>
          <div class="stat-card stat-card--realtime">
            <div class="stat-header">
              <octicon :icon="historyIcon" />
              <span>{{ $t('perHour') || '/giờ' }}</span>
            </div>
            <div class="stat-value">{{ formatNumber(stats.logsPerHour) }}</div>
          </div>
          <div class="stat-card stat-card--realtime">
            <div class="stat-header">
              <octicon :icon="calendarIcon" />
              <span>{{ $t('logRate.realtimeDayLabel') || '/ngày gần nhất · last 24h' }}</span>
            </div>
            <div class="stat-value">{{ formatNumber(stats.logsPerDay) }}</div>
          </div>
        </div>
      </div>

      <!-- Avg / Max theo range -->
      <div class="stats-section stats-section--range">
        <div class="stats-section-header">
          <div>
            <h4 class="stats-section-title">{{ $t('logRate.rangeTitle') || 'Trung bình / Cao nhất theo khoảng thời gian' }}</h4>
            <p class="stats-section-description">{{ $t('logRate.rangeDescription') || 'Avg / Max by selected range' }}</p>
          </div>
          <div class="stats-section-meta">
            <range-picker
              ref="rangePicker"
              :lookup.sync="selectedLookup"
              :range.sync="selectedDates"
              :disabled="loading"
              :enable-time="false"
              :allowed-presets="serverAllowedPresets"
              small
            />
          </div>
        </div>
        <div class="stats-grid">
          <div class="stat-card">
            <div class="stat-header">
              <octicon :icon="pulseIcon" />
              <span>{{ $t('perSecond') || '/giây' }}</span>
            </div>
            <div class="stat-metric-row">
              <span class="stat-label">{{ $t('logRate.averageLabel') || 'Trung bình / Avg' }}</span>
              <span class="stat-detail-value">{{ formatNumber(averages.logsPerSecond) }}</span>
            </div>
            <div class="stat-metric-row stat-metric-row--peak">
              <span class="stat-label">{{ $t('logRate.peakLabel') || 'Cao nhất / Max' }}</span>
              <span class="stat-detail-value">{{ formatNumber(peaks.logsPerSecond) }}</span>
            </div>
          </div>
          <div class="stat-card">
            <div class="stat-header">
              <octicon :icon="clockIcon" />
              <span>{{ $t('perMinute') || '/phút' }}</span>
            </div>
            <div class="stat-metric-row">
              <span class="stat-label">{{ $t('logRate.averageLabel') || 'Trung bình / Avg' }}</span>
              <span class="stat-detail-value">{{ formatNumber(averages.logsPerMinute) }}</span>
            </div>
            <div class="stat-metric-row stat-metric-row--peak">
              <span class="stat-label">{{ $t('logRate.peakLabel') || 'Cao nhất / Max' }}</span>
              <span class="stat-detail-value">{{ formatNumber(peaks.logsPerMinute) }}</span>
            </div>
          </div>
          <div class="stat-card">
            <div class="stat-header">
              <octicon :icon="historyIcon" />
              <span>{{ $t('perHour') || '/giờ' }}</span>
            </div>
            <div class="stat-metric-row">
              <span class="stat-label">{{ $t('logRate.averageLabel') || 'Trung bình / Avg' }}</span>
              <span class="stat-detail-value">{{ formatNumber(averages.logsPerHour) }}</span>
            </div>
            <div class="stat-metric-row stat-metric-row--peak">
              <span class="stat-label">{{ $t('logRate.peakLabel') || 'Cao nhất / Max' }}</span>
              <span class="stat-detail-value">{{ formatNumber(peaks.logsPerHour) }}</span>
            </div>
          </div>
          <div class="stat-card">
            <div class="stat-header">
              <octicon :icon="calendarIcon" />
              <span>{{ $t('perDay') || '/ngày' }}</span>
            </div>
            <div class="stat-metric-row">
              <span class="stat-label">{{ $t('logRate.averageLabel') || 'Trung bình / Avg' }}</span>
              <span class="stat-detail-value">{{ formatNumber(averages.logsPerDay) }}</span>
            </div>
            <div class="stat-metric-row stat-metric-row--peak">
              <span class="stat-label">{{ $t('logRate.peakLabel') || 'Cao nhất / Max' }}</span>
              <span class="stat-detail-value">{{ formatNumber(peaks.logsPerDay) }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Dropped Logs Warning -->
      <div v-if="stats.droppedLogs > 0" class="dropped-section notification is-warning is-light">
        <div class="dropped-header">
          <octicon :icon="alertIcon" />
          <span>{{ $t('droppedLogs') || 'Dropped Logs' }}</span>
        </div>
        <div class="dropped-stats">
          <span class="dropped-count">{{ stats.droppedLogs.toLocaleString() }} logs</span>
        </div>
        <p class="is-size-7">{{ $t('droppedLogsReason') || 'Logs dropped due to quota limit or buffer overflow' }}</p>
      </div>
    </div>

    <!-- Footer -->
    <div class="log-rate-footer">
      <span v-if="lastUpdated" class="footer-item">
        {{ $t('lastUpdated') || 'Last updated' }}: {{ formatTime(lastUpdated) }}
      </span>
      <span v-if="rangeHint" class="footer-item">
        {{ rangeHint }}
      </span>
      <span v-if="autoRefresh && !error" class="footer-item">
        {{ $t('autoRefreshIn') || 'Auto refresh in' }} {{ nextRefreshIn }}s
      </span>
    </div>

    <!-- Quota Settings Modal -->
    <quota-settings-modal
      :show="showQuotaModal"
      :current-max-bytes="stats.quotaMax"
      @update:show="showQuotaModal = $event"
      @save="handleQuotaSave"
    />

    <!-- Confirmation Dialog -->
    <confirmation-dialog
      :show="showQuotaConfirm"
      :title="$t('confirmQuotaChange') || 'Xác nhận thay đổi Quota'"
      :message="($t('confirmQuotaMessage') || 'Bạn có chắc muốn thay đổi quota thành') + ' ' + formatBytes(pendingMaxBytes) + '?'"
      :warning-text="formatBytes(pendingMaxBytes)"
      confirm-button-class="is-primary"
      @confirm="handleQuotaConfirm"
      @cancel="showQuotaConfirm = false"
    />
  </div>
</template>

<script>
import { sync, pulse, clock, history, calendar, alert, pencil } from 'octicons-vue'
import QuotaSettingsModal from '@/components/QuotaSettingsModal'
import ConfirmationDialog from '@/components/common/ConfirmationDialog'
import RangePicker from '@/components/RangePicker'

export default {
  name: 'LogRateStats',
  i18n: {
    messages: {
      en: {
        logRate: {
          selectedDaysSingle: '1 day',
          selectedDaysMultiple: '{days} days',
          presetToday: 'Today',
          presetThisWeek: 'This week',
          presetThisMonth: 'This month',
          presetThisYear: 'This year',
          presetLast24Hours: 'Last 24h',
          presetLast7Days: 'Last 7 days',
          presetLast30Days: 'Last 30 days',
          realtimeTitle: 'Realtime',
          realtimeDescription: 'Last 1 second, 1 minute, 1 hour, and 24 hours',
          rangeTitle: 'Average / Maximum by selected range',
          rangeDescription: 'Based on the selected range',
          realtimeDayLabel: 'Last 24 hours',
          averageLabel: 'Avg',
          peakLabel: 'Max',
          selectedRangeTotal: 'Total in range'
        }
      },
      vi: {
        logRate: {
          selectedDaysSingle: '1 ngày',
          selectedDaysMultiple: '{days} ngày',
          presetToday: 'Hôm nay',
          presetThisWeek: 'Tuần này',
          presetThisMonth: 'Tháng này',
          presetThisYear: 'Năm này',
          presetLast24Hours: '24 giờ gần nhất',
          presetLast7Days: '7 ngày gần nhất',
          presetLast30Days: '30 ngày gần nhất',
          realtimeTitle: 'Thời gian thực',
          realtimeDescription: '1 giây, 1 phút, 1 giờ và 24 giờ gần nhất',
          rangeTitle: 'Trung bình / Cao nhất theo khoảng thời gian',
          rangeDescription: 'Theo khoảng thời gian đã chọn',
          realtimeDayLabel: '24 giờ gần nhất',
          averageLabel: 'Trung bình',
          peakLabel: 'Cao nhất',
          selectedRangeTotal: 'Tổng trong khoảng'
        }
      }
    }
  },
  components: { QuotaSettingsModal, ConfirmationDialog, RangePicker },
  props: {
    refreshInterval: {
      type: Number,
      default: 15000 // 15 seconds
    },
    autoRefresh: {
      type: Boolean,
      default: true
    }
  },
  data () {
    return {
      stats: {
        logsPerSecond: 0,
        logsPerMinute: 0,
        logsPerHour: 0,
        logsPerDay: 0,
        logsInRange: 0,
        droppedLogs: 0,
        droppedBytes: 0,
        quotaEnabled: false,
        quotaMax: 0,
        quotaUsed: 0,
        quotaPercent: 0
      },
      averages: {
        logsPerSecond: 0,
        logsPerMinute: 0,
        logsPerHour: 0,
        logsPerDay: 0,
        logsInRange: 0
      },
      lastUpdated: null,
      loading: false,
      error: null,
      interval: null,
      nextRefreshIn: 0,
      countdownInterval: null,
      showQuotaModal: false,
      showQuotaConfirm: false,
      pendingMaxBytes: 0,
      serverAllowedPresets: ['day0', 'week0', 'month0', 'year0', 'day-1', 'day-7', 'day-30'],
      selectedLookup: 'week0',
      selectedDates: [],
      range: null,
      activeRequestId: 0,
      suppressRangeRefresh: true,
      peaks: {
        logsPerSecond: 0,
        logsPerMinute: 0,
        logsPerHour: 0,
        logsPerDay: 0,
        logsInRange: null,
        logs7d: 0,
        logs30d: 0
      }
    }
  },
  computed: {
    syncIcon () {
      return sync
    },
    pulseIcon () {
      return pulse
    },
    clockIcon () {
      return clock
    },
    historyIcon () {
      return history
    },
    calendarIcon () {
      return calendar
    },
    alertIcon () {
      return alert
    },
    pencilIcon () {
      return pencil
    },
    selectedRangeLabel () {
      switch (this.selectedLookup) {
        case 'day0':
          return this.$t('logRate.presetToday') || 'Hôm nay'
        case 'week0':
          return this.$t('logRate.presetThisWeek') || 'Tuần này'
        case 'month0':
          return this.$t('logRate.presetThisMonth') || 'Tháng này'
        case 'year0':
          return this.$t('logRate.presetThisYear') || 'Năm này'
        case 'day-1':
          return this.$t('logRate.presetLast24Hours') || '24 giờ gần nhất'
        case 'day-7':
          return this.$t('logRate.presetLast7Days') || '7 ngày gần nhất'
        case 'day-30':
          return this.$t('logRate.presetLast30Days') || '30 ngày gần nhất'
      }

      const days = this.selectedRangeDays
      if (days === 1) {
        return this.$t('logRate.selectedDaysSingle') || '1 ngày'
      }
      return this.$t('logRate.selectedDaysMultiple', { days }) || `${days} ngày`
    },
    selectedRangeDays () {
      if (this.selectedLookup === 'day-1') {
        return 1
      }
      if (this.selectedLookup === 'day-7') {
        return 7
      }
      if (this.selectedLookup === 'day-30') {
        return 30
      }

      if (!this.selectedDates || this.selectedDates.length !== 2) {
        return this.range && this.range.days ? this.range.days : 0
      }

      const normalizedRange = this.normalizeRangeForRequest(this.selectedDates)
      if (!normalizedRange) {
        return this.range && this.range.days ? this.range.days : 0
      }

      const diff = normalizedRange.end.getTime() - normalizedRange.start.getTime()
      return Math.max(1, Math.floor(diff / (24 * 60 * 60 * 1000)) + 1)
    },
    rangeHint () {
      return this.selectedRangeLabel
    }
  },
  mounted () {
    this.initializeRange()
    this.suppressRangeRefresh = false
    this.fetchStats()
    if (this.autoRefresh) {
      this.startAutoRefresh()
    }
  },
  beforeDestroy () {
    this.stopAutoRefresh()
  },
  watch: {
    selectedDates (val) {
      if (this.suppressRangeRefresh || !val || val.length !== 2) {
        return
      }

      const normalizedDates = this.normalizeRangeDates(val)
      if (
        normalizedDates[0].getTime() !== val[0].getTime() ||
        normalizedDates[1].getTime() !== val[1].getTime()
      ) {
        this.suppressRangeRefresh = true
        this.selectedDates = normalizedDates
        this.$nextTick(() => {
          this.suppressRangeRefresh = false
          this.refresh()
        })
        return
      }

      this.refresh()
    },
    selectedLookup (val) {
      if (!val || this.suppressRangeRefresh) {
        return
      }
      const now = new Date()
      this.suppressRangeRefresh = true
      this.selectedDates = this.getRangeForLookup(val, now)
      this.$nextTick(() => {
        this.suppressRangeRefresh = false
        this.refresh()
      })
    }
  },
  methods: {
    initializeRange () {
      if (this.selectedDates && this.selectedDates.length === 2) {
        return
      }

      const now = new Date()
      this.selectedDates = this.getRangeForLookup(this.selectedLookup, now)
    },

    startOfDayDate (date) {
      return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0)
    },

    endOfDayDate (date) {
      return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 0)
    },

    normalizeRangeDates (dates) {
      if (!dates || dates.length !== 2) {
        return []
      }

      const [start, end] = dates
      if (this.isRollingLookup(this.selectedLookup)) {
        return [new Date(start), new Date(end)]
      }

      return [this.startOfDayDate(start), this.endOfDayDate(end)]
    },

    normalizeRangeForRequest (dates) {
      if (!dates || dates.length !== 2) {
        return null
      }

      const normalizedDates = this.normalizeRangeDates(dates)
      return {
        start: normalizedDates[0],
        end: normalizedDates[1]
      }
    },

    isRollingLookup (lookup) {
      return ['day-1', 'day-7', 'day-30'].includes(lookup)
    },

    getRangeForLookup (lookup, now = new Date()) {
      const currentTime = new Date(now)
      switch (lookup) {
        case 'day0':
          return [this.startOfDayDate(now), currentTime]
        case 'week0': {
          const date = new Date(now)
          const day = date.getDay() || 7
          date.setDate(date.getDate() - day + 1)
          return [this.startOfDayDate(date), currentTime]
        }
        case 'month0':
          return [new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0), currentTime]
        case 'year0':
          return [new Date(now.getFullYear(), 0, 1, 0, 0, 0, 0), currentTime]
        case 'day-1': {
          const start = new Date(now)
          start.setHours(start.getHours() - 24)
          return [start, currentTime]
        }
        case 'day-7': {
          const start = new Date(now)
          start.setDate(start.getDate() - 7)
          return [start, currentTime]
        }
        case 'day-30': {
          const start = new Date(now)
          start.setDate(start.getDate() - 30)
          return [start, currentTime]
        }
        default: {
          const start = new Date(now)
          start.setDate(start.getDate() - 7)
          return [start, currentTime]
        }
      }
    },

    formatRFC3339 (date) {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')
      const seconds = String(date.getSeconds()).padStart(2, '0')
      const offsetMinutes = -date.getTimezoneOffset()
      const sign = offsetMinutes >= 0 ? '+' : '-'
      const absOffsetMinutes = Math.abs(offsetMinutes)
      const offsetHours = String(Math.floor(absOffsetMinutes / 60)).padStart(2, '0')
      const offsetRemainder = String(absOffsetMinutes % 60).padStart(2, '0')
      return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}${sign}${offsetHours}:${offsetRemainder}`
    },

    buildRequestRange () {
      this.initializeRange()
      const normalizedRange = this.normalizeRangeForRequest(this.selectedDates)
      if (!normalizedRange) {
        return null
      }
      return {
        start: this.formatRFC3339(normalizedRange.start),
        end: this.formatRFC3339(normalizedRange.end)
      }
    },

    async fetchStats () {
      const requestId = ++this.activeRequestId
      const requestRange = this.buildRequestRange()

      if (!requestRange) {
        return
      }

      try {
        this.loading = true
        this.error = null

        const response = await this.$http.get('/api/v1/system/log-rate', {
          params: requestRange
        })

        // Extract data from response - handle nested response structure
        let data = null
        if (response) {
          if (response.data && response.data.data && response.data.data.rates) {
            data = response.data.data
          } else if (response.data && response.data.rates) {
            data = response.data
          } else if (response.rates) {
            data = response
          }
        }

        if (!data || !data.rates) {
          throw new Error('Invalid response from server')
        }

        if (requestId !== this.activeRequestId) {
          return
        }

        const rates = data.rates || {}
        const quota = data.quota || {}
        const averages = data.averages || null
        const range = data.range || null

        this.stats = {
          logsPerSecond: rates.logs_per_second || 0,
          logsPerMinute: rates.logs_per_minute || 0,
          logsPerHour: rates.logs_per_hour || 0,
          logsPerDay: rates.logs_per_day || 0,
          logsInRange: rates.logs_in_range || 0,
          droppedLogs: rates.dropped_today || 0,
          droppedBytes: rates.dropped_bytes_today || 0,
          quotaEnabled: quota.enabled || false,
          quotaMax: quota.max_bytes || 0,
          quotaUsed: quota.used_bytes || 0,
          quotaPercent: quota.usage_percent || 0
        }
        this.averages = {
          logsPerSecond: averages && averages.logs_per_second ? averages.logs_per_second : 0,
          logsPerMinute: averages && averages.logs_per_minute ? averages.logs_per_minute : 0,
          logsPerHour: averages && averages.logs_per_hour ? averages.logs_per_hour : 0,
          logsPerDay: averages && averages.logs_per_day ? averages.logs_per_day : 0,
          logsInRange: averages && averages.logs_per_range ? averages.logs_per_range : 0
        }
        this.range = range
        this.lastUpdated = new Date()

        const peaks = data.peaks || null
        this.peaks = {
          logsPerSecond: peaks && peaks.logs_per_second ? peaks.logs_per_second : 0,
          logsPerMinute: peaks && peaks.logs_per_minute ? peaks.logs_per_minute : 0,
          logsPerHour: peaks && peaks.logs_per_hour ? peaks.logs_per_hour : 0,
          logsPerDay: peaks && peaks.logs_per_day ? peaks.logs_per_day : 0,
          logsInRange: peaks && peaks.logs_per_range !== undefined ? peaks.logs_per_range : null,
          logs7d: peaks && peaks.logs_per_7d ? peaks.logs_per_7d : 0,
          logs30d: peaks && peaks.logs_per_30d ? peaks.logs_per_30d : 0
        }

      } catch (error) {
        if (requestId !== this.activeRequestId) {
          return
        }
        console.error('Failed to fetch log rate stats:', error)
        this.range = null
        this.error = error.message || 'Failed to fetch stats'
      } finally {
        if (requestId === this.activeRequestId) {
          this.loading = false
        }
      }
    },


    refresh () {
      this.fetchStats()
      if (this.autoRefresh) {
        this.resetCountdown()
      }
    },

    startAutoRefresh () {
      this.resetCountdown()
      this.interval = setInterval(() => {
        this.fetchStats()
        this.resetCountdown()
      }, this.refreshInterval)
    },

    stopAutoRefresh () {
      if (this.interval) {
        clearInterval(this.interval)
        this.interval = null
      }
      if (this.countdownInterval) {
        clearInterval(this.countdownInterval)
        this.countdownInterval = null
      }
    },

    resetCountdown () {
      this.nextRefreshIn = Math.floor(this.refreshInterval / 1000)
      if (this.countdownInterval) {
        clearInterval(this.countdownInterval)
      }
      this.countdownInterval = setInterval(() => {
        this.nextRefreshIn = Math.max(0, this.nextRefreshIn - 1)
      }, 1000)
    },

    formatBytes (bytes) {
      if (bytes === 0) return '0 B'
      const k = 1024
      const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    },

    formatNumber (value) {
      if (value === null || value === undefined) return '0'
      if (value >= 1000000) {
        return (value / 1000000).toFixed(2) + 'M'
      }
      if (value >= 1000) {
        return (value / 1000).toFixed(2) + 'K'
      }
      return value.toFixed(0)
    },

    formatPercent (value) {
      if (value === null || value === undefined) return '0%'
      return value.toFixed(1) + '%'
    },

    formatTime (date) {
      if (!date) return ''
      return date.toLocaleTimeString()
    },

    getQuotaProgressClass (percent) {
      if (percent >= 90) return 'is-danger'
      if (percent >= 70) return 'is-warning'
      return 'is-success'
    },

    handleQuotaSave (maxBytes) {
      this.pendingMaxBytes = maxBytes
      this.showQuotaModal = false
      this.showQuotaConfirm = true
    },

    async handleQuotaConfirm () {
      this.showQuotaConfirm = false
      try {
        await this.$http.put('/api/v1/system/settings/quota', {
          max_bytes: this.pendingMaxBytes
        })
        if (this.$toast) {
          this.$toast(this.$t('quotaUpdated') || 'Quota đã được cập nhật', 'success', 3000)
        }
        this.fetchStats()
      } catch (err) {
        const msg = (err && err.body && err.body.message) || (this.$t('quotaUpdateFailed') || 'Không thể cập nhật quota')
        if (this.$toast) {
          this.$toast(msg, 'error', 5000)
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.log-rate-widget {
  padding: 1rem;
  border-radius: 6px;
  background: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  .is-dark & {
    background: #2c2c2c;
    color: #eee;
  }

  .log-rate-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 0.75rem;
    margin-bottom: 1rem;
    padding-bottom: 0.5rem;
    border-bottom: 1px solid rgba(0, 0, 0, 0.1);

    .is-dark & {
      border-bottom-color: rgba(255, 255, 255, 0.1);
    }
  }

  .log-rate-header-main {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  .log-rate-header-actions {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .log-rate-title {
    font-weight: 600;
    font-size: 1rem;
  }

  .log-rate-range-hint {
    font-size: 0.75rem;
    color: #888;

    .is-dark & {
      color: #aaa;
    }
  }

  .log-rate-range-presets {
    display: flex;
    gap: 0.35rem;
  }

  .range-preset-btn {
    border: 1px solid #ddd;
    background: transparent;
    border-radius: 999px;
    padding: 0.2rem 0.55rem;
    font-size: 0.75rem;
    cursor: pointer;
    color: inherit;

    &.is-active {
      background: #3273dc;
      border-color: #3273dc;
      color: #fff;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    .is-dark & {
      border-color: #555;
    }
  }

  .log-rate-refresh-btn {
    background: transparent;
    border: 1px solid #ddd;
    border-radius: 4px;
    padding: 0.25rem 0.5rem;
    cursor: pointer;
    color: inherit;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
      background: rgba(0, 0, 0, 0.05);
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.5;
    }

    .is-dark & {
      border-color: #555;

      &:hover {
        background: rgba(255, 255, 255, 0.1);
      }
    }
  }

  .log-rate-content {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .quota-section {
    padding: 0.75rem;
    background: rgba(0, 0, 0, 0.02);
    border-radius: 4px;

    .is-dark & {
      background: rgba(255, 255, 255, 0.05);
    }

    .quota-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 0.5rem;
    }

    .quota-title {
      font-weight: 600;
      font-size: 0.875rem;
    }

    .quota-usage {
      font-size: 0.75rem;
      color: #666;

      .is-dark & {
        color: #aaa;
      }
    }

    .quota-header-right {
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }

    .edit-quota-btn {
      background: transparent;
      border: 1px solid #ddd;
      border-radius: 4px;
      padding: 0.15rem 0.35rem;
      cursor: pointer;
      color: inherit;
      display: flex;
      align-items: center;

      &:hover {
        background: rgba(0, 0, 0, 0.05);
      }

      .is-dark & {
        border-color: #555;
        &:hover {
          background: rgba(255, 255, 255, 0.1);
        }
      }
    }

    .progress-container {
      position: relative;
      margin-bottom: 0.25rem;

      .progress {
        height: 1.25rem;
        border-radius: 4px;
      }

      .progress-label {
        position: absolute;
        right: 0.5rem;
        top: 50%;
        transform: translateY(-50%);
        font-size: 0.75rem;
        font-weight: bold;
        color: #333;
        text-shadow: 0 0 3px #fff, 0 0 3px #fff, 0 0 3px #fff;

        .is-dark & {
          color: #fff;
          text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
        }
      }
    }

    .quota-warning {
      display: flex;
      align-items: center;
      gap: 0.25rem;
      margin-top: 0.5rem;
    }
  }

  .stats-section {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .stats-section--range {
    padding-top: 0.25rem;
    border-top: 1px solid rgba(0, 0, 0, 0.06);

    .is-dark & {
      border-top-color: rgba(255, 255, 255, 0.08);
    }
  }

  .stats-section-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 0.75rem;
    flex-wrap: wrap;
  }

  .stats-section-title {
    font-size: 0.95rem;
    font-weight: 600;
    color: #333;
    margin: 0;

    .is-dark & {
      color: #fff;
    }
  }

  .stats-section-description {
    margin: 0.2rem 0 0;
    font-size: 0.75rem;
    color: #888;

    .is-dark & {
      color: #aaa;
    }
  }

  .stats-section-meta {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 0.25rem;

    @media (max-width: 767px) {
      align-items: flex-start;
    }
  }

  .stats-range-chip {
    display: inline-flex;
    align-items: center;
    border-radius: 999px;
    padding: 0.2rem 0.55rem;
    font-size: 0.72rem;
    font-weight: 600;
    background: rgba(50, 115, 220, 0.1);
    color: #3273dc;

    .is-dark & {
      background: rgba(50, 115, 220, 0.18);
      color: #72a7f7;
    }
  }

  .stats-range-total {
    font-size: 0.72rem;
    color: #777;
    text-align: right;

    .is-dark & {
      color: #aaa;
    }
  }

  .stats-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.75rem;

    @media (min-width: 768px) {
      grid-template-columns: repeat(4, minmax(0, 1fr));
    }
  }

  .stat-card {
    padding: 0.85rem;
    background: rgba(0, 0, 0, 0.02);
    border-radius: 4px;

    .is-dark & {
      background: rgba(255, 255, 255, 0.05);
    }
  }

  .stat-card--realtime {
    text-align: center;
  }

  .stat-header {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.25rem;
    font-size: 0.75rem;
    color: #666;
    margin-bottom: 0.35rem;
    min-height: 2rem;

    .is-dark & {
      color: #aaa;
    }
  }

  .stat-value {
    font-size: 1.5rem;
    font-weight: 700;
    color: #333;

    .is-dark & {
      color: #fff;
    }
  }

  .stat-metric-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    font-size: 0.78rem;
    color: #666;
    margin-top: 0.35rem;

    .is-dark & {
      color: #b8b8b8;
    }
  }

  .stat-metric-row--peak {
    padding-top: 0.35rem;
    border-top: 1px dashed rgba(0, 0, 0, 0.08);

    .is-dark & {
      border-top-color: rgba(255, 255, 255, 0.1);
    }
  }

  .stat-label {
    color: #888;
    line-height: 1.2;

    .is-dark & {
      color: #9f9f9f;
    }
  }

  .stat-detail-value {
    font-weight: 700;
    color: #333;

    .is-dark & {
      color: #fff;
    }
  }

  .dropped-section {
    margin-top: 0.5rem;

    .dropped-header {
      display: flex;
      align-items: center;
      gap: 0.25rem;
      font-weight: 600;
      margin-bottom: 0.25rem;
    }

    .dropped-stats {
      .dropped-count {
        font-size: 1.25rem;
        font-weight: 700;
      }

      .dropped-bytes {
        font-size: 0.875rem;
        color: #666;
        margin-left: 0.5rem;
      }
    }
  }

  .log-rate-footer {
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 0.35rem 0.75rem;
    margin-top: 1rem;
    padding-top: 0.5rem;
    border-top: 1px solid rgba(0, 0, 0, 0.1);
    font-size: 0.75rem;
    color: #888;

    .is-dark & {
      border-top-color: rgba(255, 255, 255, 0.1);
      color: #aaa;
    }

    .footer-item {
      display: inline-block;
    }
  }

  .spin {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
}
</style>
